import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Image, Film, Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";

const MediaPage = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    const fetchMedia = async () => {
      try {
        const response = await axios.get(`${API}/media?limit=100`);
        setMedia(response.data);
      } catch (error) {
        console.error("Error fetching media:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMedia();
  }, []);

  const photos = media.filter(m => m.type === 'photo');
  const videos = media.filter(m => m.type === 'video');

  const filteredPhotos = photos.filter(m => 
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const filteredVideos = videos.filter(m => 
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (m.description && m.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const placeholderImages = [
    "https://images.unsplash.com/photo-1558465202-92356bf74344?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    "https://images.unsplash.com/photo-1574629810360-7efbbe195018?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    "https://images.unsplash.com/photo-1526232761682-d26e03ac148e?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
    "https://images.unsplash.com/photo-1705593973313-75de7bf95b56?crop=entropy&cs=srgb&fm=jpg&q=85&w=400",
  ];

  return (
    <div data-testid="media-page">
      {/* Hero Section */}
      <section className="bg-[#09090b] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Image className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">
            Media Gallery
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-8">
            Photos and videos from ABFA matches, events, and behind the scenes.
          </p>
          
          {/* Search */}
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search media..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 bg-white/10 border-0 text-white placeholder:text-gray-500 rounded-none"
              data-testid="media-search-input"
            />
          </div>
        </div>
      </section>

      {/* Media Content */}
      <section className="py-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="photos" className="w-full">
            <TabsList className="w-full justify-start border-b border-gray-200 mb-8 bg-transparent h-auto p-0">
              <TabsTrigger 
                value="photos"
                className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent flex items-center gap-2"
                data-testid="tab-photos"
              >
                <Image className="w-5 h-5" />
                Photos ({photos.length || placeholderImages.length})
              </TabsTrigger>
              <TabsTrigger 
                value="videos"
                className="px-6 py-4 font-heading font-bold uppercase text-gray-500 data-[state=active]:text-[#09090b] data-[state=active]:border-b-4 data-[state=active]:border-[#FFC72C] rounded-none bg-transparent flex items-center gap-2"
                data-testid="tab-videos"
              >
                <Film className="w-5 h-5" />
                Videos ({videos.length})
              </TabsTrigger>
            </TabsList>

            {/* Photos Tab */}
            <TabsContent value="photos">
              {loading ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {[1,2,3,4,5,6,7,8].map(i => (
                    <Skeleton key={i} className="aspect-square" />
                  ))}
                </div>
              ) : (filteredPhotos.length > 0 || (searchQuery === "" && photos.length === 0)) ? (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {(filteredPhotos.length > 0 ? filteredPhotos : placeholderImages.map((url, i) => ({
                    media_id: `placeholder-${i}`,
                    title: `ABFA Football ${i + 1}`,
                    url: url,
                    type: 'photo'
                  }))).map((item) => (
                    <Dialog key={item.media_id}>
                      <DialogTrigger asChild>
                        <div 
                          className="aspect-square overflow-hidden cursor-pointer group"
                          data-testid={`photo-${item.media_id}`}
                        >
                          <img 
                            src={item.url || item.thumbnail_url} 
                            alt={item.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                        </div>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl p-0 bg-black border-0">
                        <img 
                          src={item.url} 
                          alt={item.title}
                          className="w-full h-auto"
                        />
                        <div className="p-4 bg-black text-white">
                          <h3 className="font-heading font-bold text-lg">{item.title}</h3>
                          {item.description && (
                            <p className="text-gray-400 text-sm mt-1">{item.description}</p>
                          )}
                        </div>
                      </DialogContent>
                    </Dialog>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">No Photos Found</h3>
                  <p className="text-gray-500">Try adjusting your search criteria.</p>
                </div>
              )}
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos">
              {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[1,2,3,4,5,6].map(i => (
                    <Skeleton key={i} className="aspect-video" />
                  ))}
                </div>
              ) : filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredVideos.map((video) => (
                    <div key={video.media_id} className="card-abfa group" data-testid={`video-${video.media_id}`}>
                      <div className="aspect-video relative overflow-hidden bg-black">
                        {video.url.includes('youtube') ? (
                          <iframe
                            src={video.url}
                            title={video.title}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        ) : (
                          <video 
                            src={video.url} 
                            poster={video.thumbnail_url}
                            controls
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                      <div className="p-4">
                        <h3 className="font-heading font-bold uppercase text-[#09090b]">{video.title}</h3>
                        {video.description && (
                          <p className="text-gray-500 text-sm mt-1 line-clamp-2">{video.description}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Film className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading font-bold text-2xl text-gray-400 uppercase mb-2">No Videos Yet</h3>
                  <p className="text-gray-500">Check back soon for video highlights.</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default MediaPage;
